// lineArcSideTools.js — Bespoke draw interactions for LINE arc-side selector rules.
// LINE10, LINE24, LINE33
//
// P3 is a side-selector: its position relative to the P1–P2 line determines
// which side the arc is drawn on, and its distance sets the arc diameter.
// Sketch: live flipping arc preview as cursor crosses the P1–P2 perpendicular.
//
// Point-order contracts (from mil-sym-ts-web DrawRules.d.mts / drawRuleMeta.js):
//   - LINE10 / LINE24: P1 = arrowhead tip, P2 = rear of symbol, P3 = arc side.
//     The natural placement gesture is "click my own position (rear) first, then
//     click toward the target (tip), then pick the arc side" — the OPPOSITE of
//     the library's P1=tip order — so the first two collected points are reversed
//     on finish (same fix as LINE9/20/25/28 in directionalArrowTools.js and
//     AXIS1/2 in miscBespokeTools.js). Without this the rendered arrow points
//     back at the user's start instead of at their target.
//   - LINE33: P1/P2 are a plain spine (beginning/end of the straight line); the
//     arrowhead tip lives entirely at P3 (already the last/aimed click). No
//     tip/rear asymmetry on the spine, so no reversal is needed — same category
//     as LINE4/LINE19 in directionalArrowTools.js's header comment.
//
// Live mil-sym preview: wired for all three rules. getCollected mirrors finish()'s
// ordering so the live symbol doesn't point backwards while the user aims P3.
// minPoints=2 because the renderer needs 3 points total (2 collected + cursor as
// provisional P3) before a meaningful preview is possible.

import { DrawRules } from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import Feature from 'ol/Feature';
import { LineString, Circle as OLCircle } from 'ol/geom';
import { registerDrawInteraction } from '../customDrawInteractions';
import { createSequentialCollector, reverseFirstTwo } from './sequentialCollector';
import { distanceToInfiniteLine } from './geometryUtils';
import { PREVIEW_LINE_BLUE, PREVIEW_LINE_AMBER_DASHED } from './drawPreviewStyles';

// For reverseFirstTwo rules: click 1 = rear (anchor), click 2 = tip (direction —
// the aimed target), click 3 = arc side (direction). For LINE33 both spine points
// stay 'anchor' and only P3 is 'direction'.
function roleForIndex(i, doReverseFirstTwo) {
    if (doReverseFirstTwo) return i === 0 ? 'anchor' : 'direction';
    return i < 2 ? 'anchor' : 'direction';
}

function makeArcSideCollector(map, opts, { doReverseFirstTwo }) {
    return createSequentialCollector(map, {
        minCount: 3,
        maxCount: 3,
        onFinish: opts.onFinish,
        reorderOnFinish: doReverseFirstTwo ? reverseFirstTwo : (c) => c,
        sidc: opts.sidc || null,
        previewMinPoints: 2,
        cursorFirst: false,
        // While preview is live at most 2 points are collected (auto-completes at 3);
        // reverseFirstTwo([A,B]) === reverse([A,B]), matching prior getCollected wiring.
        applyReorderToPreview: doReverseFirstTwo,
        roleForIndex: (i) => roleForIndex(i, doReverseFirstTwo),
        styleResolver: (f) => (f.get('_t') === 'arc' ? PREVIEW_LINE_AMBER_DASHED : PREVIEW_LINE_BLUE),
        onSketchUpdate: ({ previewSource, collected3857, cursorCoord3857 }) => {
            const pts = cursorCoord3857 ? [...collected3857, cursorCoord3857] : collected3857;
            if (pts.length >= 2) {
                const lf = new Feature(new LineString(pts.slice(0, 2)));
                lf.set('_t', 'line');
                previewSource.addFeature(lf);
            }
            // Arc side preview after 2 pts placed
            if (collected3857.length >= 2 && cursorCoord3857) {
                const [a, b] = collected3857;
                const d = distanceToInfiniteLine(cursorCoord3857[0], cursorCoord3857[1], a[0], a[1], b[0], b[1]);
                // Draw a circle at midpoint of spine with radius = d as arc indicator
                const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
                const cf = new Feature(new OLCircle([mx, my], d));
                cf.set('_t', 'arc');
                previewSource.addFeature(cf);
            }
        },
    });
}

// LINE10/LINE24: P1=arrowhead tip, P2=rear — natural gesture is rear-first,
// tip-second, so the first two clicks must be reversed before handing off.
registerDrawInteraction(DrawRules.LINE10, (map, opts) => makeArcSideCollector(map, opts, { doReverseFirstTwo: true }));
registerDrawInteraction(DrawRules.LINE24, (map, opts) => makeArcSideCollector(map, opts, { doReverseFirstTwo: true }));

// LINE33: tip lives at P3 already — no reversal.
registerDrawInteraction(DrawRules.LINE33, (map, opts) => makeArcSideCollector(map, opts, { doReverseFirstTwo: false }));
